import { cn } from "@/lib/utils";

interface StepperProps {
  currentStep: number;
  totalSteps: number;
  stepNames?: string[];
  onStepClick?: (step: number) => void;
}

export const Stepper = ({ currentStep, totalSteps, stepNames, onStepClick }: StepperProps) => {
  const handleStepClick = (step: number) => {
    if (step < currentStep && onStepClick) {
      onStepClick(step);
    }
  };

  const progress = ((currentStep - 1) / (totalSteps - 1)) * 100;

  return (
    <div className="w-full space-y-4">
      {/* Step indicators */}
      <div className="flex items-center justify-between relative">
        {/* Connection line */}
        <div className="absolute left-0 right-0 h-0.5 bg-border top-1/2 -translate-y-1/2 transition-all duration-300" />
        <div 
          className="absolute left-0 h-0.5 bg-primary top-1/2 -translate-y-1/2 transition-all duration-700 ease-out"
          style={{ width: `${progress}%` }}
        />
        
        {/* Steps */}
        {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => {
          const isCompleted = step < currentStep;
          const isCurrent = step === currentStep;
          const isClickable = step < currentStep && onStepClick;
          
          return (
            <button
              key={step}
              onClick={() => isClickable && handleStepClick(step)}
              disabled={!isClickable}
              className={cn(
                "relative z-10 flex h-10 w-10 items-center justify-center rounded-full border-2 transition-all duration-500 ease-out",
                isCompleted && "bg-primary border-primary text-primary-foreground shadow-lg shadow-primary/30",
                isCurrent && "bg-background border-primary text-primary scale-110 shadow-lg shadow-primary/20 animate-pulse",
                !isCompleted && !isCurrent && "bg-background border-border text-muted-foreground",
                isClickable && "hover:scale-110 cursor-pointer hover:border-primary/70 hover:shadow-md transition-all duration-300",
                !isClickable && "cursor-default"
              )}
            >
              <span className="text-sm font-semibold transition-transform duration-300">
                {step}
              </span>
            </button>
          );
        })}
      </div>

      {/* Step names */}
      {stepNames && (
        <div className="flex items-center justify-between">
          {stepNames.map((name, index) => (
            <div
              key={index}
              className={cn(
                "text-xs font-medium transition-all duration-500 text-center flex-1",
                index + 1 === currentStep && "text-primary font-semibold scale-105",
                index + 1 < currentStep && "text-muted-foreground",
                index + 1 > currentStep && "text-muted-foreground/50"
              )}
            >
              {name}
            </div>
          ))}
        </div>
      )}

      {/* Progress text */}
      <div className="text-center transition-all duration-500">
        <p className="text-sm text-muted-foreground">
          Schritt {currentStep} von {totalSteps}
        </p>
      </div>
    </div>
  );
};
